import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { Colors } from "../../../constants/styles";
import { Loader, SearchBar } from "../../../components";
import { Container, Card, Form, Row, Col } from "react-bootstrap";
import {
  Box,
  Alert,
  Button,
  Snackbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NumericFormat } from "react-number-format";
import useScreenWidth from "../../../hooks/useScreenWidth";

const useStyles = makeStyles({
  root: {
    "& .MuiTableCell-head": {
      color: "white",
      backgroundColor: Colors.blue700,
    },
  },
  tableRightBorder: {
    borderWidth: 0,
    borderRightWidth: 1,
    borderColor: "white",
    borderStyle: "solid",
  },
});

const toIsoDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const dateText = String(value).trim();
  const indonesiaDate = dateText.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (indonesiaDate) {
    const [, day, month, year] = indonesiaDate;
    return `${year}-${month}-${day}`;
  }

  const isoDate = dateText.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return isoDate[0];

  const parsedDate = new Date(dateText);
  if (Number.isNaN(parsedDate.getTime())) return "";
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const UbahHasilStockOpname = () => {
  const screenSize = useScreenWidth();
  const { user, dispatch } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [validated, setValidated] = useState(false);
  const [noBukti, setNoBukti] = useState("");
  const [inputTglHasilStockOpname, setInputTglHasilStockOpname] = useState("");
  const [kodeCOA, setKodeCOA] = useState("");
  const [listHasilStockOpnameChild, setListHasilStockOpnameChild] = useState(
    [],
  );
  const [stokPerCabangsData, setStokPerCabangsData] = useState([]);

  const [openCOA, setOpenCOA] = useState(false);
  const [searchTermCOA, setSearchTermCOA] = useState("");
  const [error, setError] = useState(false);
  const [COAsData, setCOAsData] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const classes = useStyles();

  const handleClickOpenCOA = () => {
    setOpenCOA(true);
  };

  const handleCloseCOA = () => {
    setOpenCOA(false);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const tempPostsCOA = COAsData.filter((val) => {
    if (searchTermCOA === "") {
      return val;
    } else if (
      val.kodeCOA.toUpperCase().includes(searchTermCOA.toUpperCase()) ||
      val.namaCOA.toUpperCase().includes(searchTermCOA.toUpperCase())
    ) {
      return val;
    }
  });

  useEffect(() => {
    getHasilStockOpnameById();
    getCOAsData();
  }, []);

  const getHasilStockOpnameById = async () => {
    setLoading(true);
    const pickedHasilStockOpname = await axios.post(
      `${tempUrl}/hasilStockOpnames/${id}`,
      {
        _id: user.id,
        token: user.token,
      },
    );
    setNoBukti(pickedHasilStockOpname.data.noBukti);
    setInputTglHasilStockOpname(
      pickedHasilStockOpname.data.tglHasilStockOpname,
    );
    setKodeCOA(pickedHasilStockOpname.data.coa.kodeCOA);
    const [child, stockData] = await Promise.all([
      axios.post(`${tempUrl}/hasilStockOpnamesChildByHasilStockOpname`, {
        hasilStockOpnameId: pickedHasilStockOpname.data.id,
        kodeCabang: user.cabang.id,
        _id: user.id,
        token: user.token,
      }),
      getStokPerCabangsData(
        pickedHasilStockOpname.data.tglHasilStockOpname,
        pickedHasilStockOpname.data.id,
      ),
    ]);
    const historicalStocks = new Map(
      stockData.map((item) => [String(item.stokId), item]),
    );
    setListHasilStockOpnameChild(
      child.data.map((item) => {
        const historicalStock = historicalStocks.get(String(item.stokId));
        const quantityLama = Number(
          historicalStock?.quantityStok ?? item.quantityLama ?? 0,
        );
        const quantityBaru =
          Number(item.quantityBaru || 0) === 0
            ? ""
            : Number(item.quantityBaru);
        return {
          ...item,
          quantityLama,
          quantitySelisih: quantityLama - Number(quantityBaru || 0),
          quantityBaru,
        };
      }),
    );
    setLoading(false);
  };

  const getCOAsData = async () => {
    setLoading(true);
    const allCOAs = await axios.post(`${tempUrl}/COAs`, {
      _id: user.id,
      token: user.token,
      kodeCabang: user.cabang.id,
    });
    setCOAsData(allCOAs.data);
    setLoading(false);
  };

  const getStokPerCabangsData = async (tanggal, hasilStockOpnameId) => {
    const allStokPerCabangs = await axios.post(
      `${tempUrl}/stokPerCabangsHasilStockOpname`,
      {
        _id: user.id,
        token: user.token,
        kodeCabang: user.cabang.id,
        tglHasilStockOpname: tanggal,
        hasilStockOpnameId,
      },
    );
    setStokPerCabangsData(allStokPerCabangs.data);
    return allStokPerCabangs.data;
  };

  const updateHasilStockOpname = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    const tanggalUpdate = toIsoDate(inputTglHasilStockOpname);
    if (form.checkValidity() && kodeCOA.length !== 0 && tanggalUpdate) {
      setLoading(true);
      try {
        setLoading(true);
        let tempCOA = await axios.post(`${tempUrl}/COAByKode`, {
          kodeCOA,
          kodeCabang: user.cabang.id,
          _id: user.id,
          token: user.token,
        });
        await axios.post(`${tempUrl}/updateHasilStockOpname/${id}`, {
          coaId: tempCOA.data.id,
          tglHasilStockOpname: tanggalUpdate,
          listHasilStockOpnameChild: listHasilStockOpnameChild.filter(
            (item) => item.stokId,
          ),
          userIdUpdate: user.id,
          _id: user.id,
          token: user.token,
        });
        setLoading(false);
        navigate(`/daftarHasilStockOpname/hasilStockOpname/${id}`);
      } catch (error) {
        alert(error.response.data.message);
      }
      setLoading(false);
    } else {
      setError(true);
      setOpen(!open);
    }
    setValidated(true);
  };

  if (loading) {
    return <Loader />;
  }

  const textRight = {
    textAlign: screenSize >= 650 && "right",
  };

  return (
    <Container>
      <h3>Transaksi</h3>
      <h5 style={{ fontWeight: 400 }}>Ubah Hasil Stock Opname</h5>
      <Typography sx={subTitleText}>
        Periode : {user.tutupperiode.namaPeriode}
      </Typography>
      <hr />
      <Card>
        <Card.Header>Hasil Stock Opname</Card.Header>
        <Card.Body>
          <Form
            noValidate
            validated={validated}
            onSubmit={updateHasilStockOpname}
          >
            <Row>
              <Col sm={6}>
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextPassword"
                >
                  <Form.Label column sm="3" style={textRight}>
                    No. Bukti :
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control required value={noBukti} disabled readOnly />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextPassword"
                >
                  <Form.Label column sm="3" style={textRight}>
                    Tanggal :
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      required
                      value={inputTglHasilStockOpname}
                      disabled
                      readOnly
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextPassword"
                >
                  <Form.Label column sm="3" style={textRight}>
                    Kode COA <b style={colorRed}>*</b> :
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      required
                      value={kodeCOA}
                      readOnly
                      placeholder="Pilih..."
                      onClick={() => handleClickOpenCOA()}
                      isInvalid={kodeCOA.length === 0 && true}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Card style={{ marginTop: 10 }}>
              <Card.Header>Detail Stok</Card.Header>
              <Card.Body>
                <Box sx={spacingTop}>
                  <Button
                    variant="contained"
                    color="info"
                    sx={{
                      bgcolor: "info.light",
                      textTransform: "none",
                      marginRight: 2,
                      marginBottom: 2,
                    }}
                    onClick={() =>
                      setListHasilStockOpnameChild((current) => [
                        ...current,
                        {
                          stokId: "",
                          quantityLama: 0,
                          quantityBaru: "",
                          quantitySelisih: 0,
                        },
                      ])
                    }
                  >
                    {"+ Input Stok"}
                  </Button>
                </Box>
                <TableContainer component={Paper} sx={{ width: "100%" }}>
                  <Table aria-label="simple table">
                    <TableHead className={classes.root}>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          className={classes.tableRightBorder}
                        >
                          Stok
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          className={classes.tableRightBorder}
                        >
                          Quantity Sistem
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          className={classes.tableRightBorder}
                        >
                          Hasil SO
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          className={classes.tableRightBorder}
                        >
                          Quantity Selisih
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          className={classes.tableRightBorder}
                        >
                          Hapus
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listHasilStockOpnameChild.map((child, index) => (
                        <TableRow key={child.id || index}>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              disablePortal
                              options={stokPerCabangsData}
                              getOptionLabel={(option) =>
                                option.stok
                                  ? `${option.stok.kodeStok} - ${option.stok.namaStok}`
                                  : ""
                              }
                              isOptionEqualToValue={(option, value) =>
                                String(option.stokId) === String(value.stokId)
                              }
                              value={
                                stokPerCabangsData.find(
                                  (item) =>
                                    String(item.stokId) ===
                                    String(child.stokId),
                                ) || null
                              }
                              renderInput={(params) => (
                                <TextField
                                  size="small"
                                  sx={{ width: "300px" }}
                                  placeholder="Cari stok..."
                                  {...params}
                                />
                              )}
                              onChange={(event, value) => {
                                const quantityLama = Number(
                                  value?.quantityStok || 0,
                                );
                                child.stokId = value?.stokId || "";
                                child.quantityLama = quantityLama;
                                child.quantitySelisih =
                                  quantityLama -
                                  Number(child.quantityBaru || 0);
                                setListHasilStockOpnameChild([
                                  ...listHasilStockOpnameChild,
                                ]);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <NumericFormat
                              value={child.quantityLama}
                              decimalSeparator=","
                              thousandSeparator="."
                              customInput={Form.Control}
                              disabled
                            />
                          </TableCell>
                          <TableCell>
                            <NumericFormat
                              value={child.quantityBaru}
                              decimalSeparator=","
                              thousandSeparator="."
                              customInput={Form.Control}
                              onValueChange={(values) => {
                                child.quantityBaru =
                                  values.value === ""
                                    ? ""
                                    : values.floatValue || 0;
                                child.quantitySelisih =
                                  Number(child.quantityLama || 0) -
                                  Number(child.quantityBaru || 0);
                                setListHasilStockOpnameChild([
                                  ...listHasilStockOpnameChild,
                                ]);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <NumericFormat
                              value={child.quantitySelisih}
                              decimalSeparator=","
                              thousandSeparator="."
                              customInput={Form.Control}
                              disabled
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="error"
                              sx={{ textTransform: "none" }}
                              onClick={() =>
                                setListHasilStockOpnameChild(
                                  listHasilStockOpnameChild.filter(
                                    (_, childIndex) => childIndex !== index,
                                  ),
                                )
                              }
                            >
                              <DeleteOutlineIcon />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card.Body>
            </Card>
            <Box sx={spacingTop}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() =>
                  navigate(`/daftarHasilStockOpname/hasilStockOpname/${id}`)
                }
                sx={{ marginRight: 2 }}
              >
                {"< Kembali"}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                type="submit"
              >
                Simpan
              </Button>
            </Box>
          </Form>
        </Card.Body>
      </Card>
      {error && (
        <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={alertBox}>
            Data belum terisi semua!
          </Alert>
        </Snackbar>
      )}
      <Dialog
        open={openCOA}
        onClose={handleCloseCOA}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`Pilih Data COA`}</DialogTitle>
        <DialogActions>
          <Box sx={dialogContainer}>
            <SearchBar setSearchTerm={setSearchTermCOA} />
            <TableContainer component={Paper} sx={dialogWrapper}>
              <Table aria-label="simple table">
                <TableHead className={classes.root}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold" }}
                      className={classes.tableRightBorder}
                    >
                      Kode COA
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold" }}
                      className={classes.tableRightBorder}
                    >
                      Nama
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tempPostsCOA
                    .filter((val) => {
                      if (searchTermCOA === "") {
                        return val;
                      } else if (
                        val.kodeCOA
                          .toUpperCase()
                          .includes(searchTermCOA.toUpperCase()) ||
                        val.namaCOA
                          .toUpperCase()
                          .includes(searchTermCOA.toUpperCase())
                      ) {
                        return val;
                      }
                    })
                    .map((user, index) => (
                      <TableRow
                        key={user._id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { bgcolor: Colors.grey300 },
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setKodeCOA(user.kodeCOA);
                          handleCloseCOA();
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {user.kodeCOA}
                        </TableCell>
                        <TableCell>{user.namaCOA}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UbahHasilStockOpname;

const spacingTop = {
  mt: 4,
};

const alertBox = {
  width: "100%",
};

const subTitleText = {
  fontWeight: "900",
};

const dialogContainer = {
  display: "flex",
  flexDirection: "column",
  width: "800px",
};

const dialogWrapper = {
  width: "100%",
  marginTop: 2,
};

const colorRed = {
  color: "red",
};
